import { useCallback, useEffect, useState } from "react";
import { createBlankCaptain, leviathanCaptainSchema } from "@personnel/rules-leviathan";
import type { LeviathanCaptain } from "@personnel/rules-leviathan";

const STORAGE_KEY = "personnel:leviathan:draft";

function loadDraft(): LeviathanCaptain {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return createBlankCaptain();
    const parsed = leviathanCaptainSchema.safeParse(JSON.parse(raw));
    // A draft written by an older build may no longer fit the schema. Starting
    // fresh is better than crashing on load.
    return parsed.success ? parsed.data : createBlankCaptain();
  } catch {
    return createBlankCaptain();
  }
}

export function useCaptainDraft() {
  const [captain, setCaptain] = useState<LeviathanCaptain>(loadDraft);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(captain));
    } catch {
      // Private browsing or a full quota. The draft simply will not persist.
    }
  }, [captain]);

  const update = useCallback((patch: Partial<LeviathanCaptain>) => {
    setCaptain((current) => ({ ...current, ...patch }));
  }, []);

  const setAttribute = useCallback((id: keyof LeviathanCaptain["attributes"], value: number) => {
    setCaptain((current) => ({
      ...current,
      attributes: { ...current.attributes, [id]: value },
    }));
  }, []);

  const toggleQuality = useCallback((id: string) => {
    setCaptain((current) => ({
      ...current,
      qualities: current.qualities.includes(id)
        ? current.qualities.filter((q) => q !== id)
        : [...current.qualities, id],
    }));
  }, []);

  // A whole-record replace. `update` merges, which would leave fields from the
  // previous captain in place if an imported record ever lacked one.
  const load = useCallback((next: LeviathanCaptain) => setCaptain(next), []);

  const reset = useCallback(() => setCaptain(createBlankCaptain()), []);

  return { captain, update, setAttribute, toggleQuality, load, reset };
}
